/**
 * Building a deal at the till.
 *
 * A deal is made of parts — "Pizza × 3", "Loaded fries × 1". A part with one
 * fixed item fills itself; a part with a choice asks which items. And an item
 * that carries its own choices — a pizza with a size — arrives with the deal's
 * saved default already applied (e.g. "Margarita · 13 Inch"), which the cashier
 * can change per unit, so three pizzas in a deal can each end up a different
 * size. This asks all of that, holds each part to its quota, and hands back the
 * flat list of items (with their choices) the customer chose — what goes on the
 * bill under the deal and to the kitchen to cook.
 *
 * The deal is charged at its own one price whatever is picked, so there is no
 * running total to chase here — only the quotas to fill.
 */
import { useState } from 'react';
import { formatPKR } from '@foodrun/shared';

import { Button } from '../../components/ui/Button.jsx';
import { ChoicePicker } from './ChoicePicker.jsx';

export function DealPicker({ product, deal, modifierGroups = {}, onCancel, onDone }) {
  const slots = deal.slots ?? [];
  const groupsFor = (productId) => modifierGroups[productId] ?? [];
  // A part fills itself only when it is one item with no choices of its own.
  const isFixed = (slot) =>
    slot.options.length === 1 && groupsFor(slot.options[0].productId).length === 0;

  // Turn an item's saved default option ids into { optionId, name } — what a
  // configured unit holds, so the default reads and travels like a live choice.
  const resolveMods = (productId, ids) => {
    if (!ids || ids.length === 0) return [];
    const names = new Map();
    for (const g of groupsFor(productId))
      for (const o of g.options ?? []) names.set(o.id, o.name);
    return ids.filter((id) => names.has(id)).map((id) => ({ optionId: id, name: names.get(id) }));
  };

  // Each part holds a list of configured units: { productId, name, modifiers }.
  // A part with a single item fills itself, with that item's default choices for
  // the deal already applied — the cashier can still change them.
  const [units, setUnits] = useState(() => {
    const start = {};
    for (const slot of slots) {
      start[slot.id] = [];
      if (slot.options.length === 1) {
        const only = slot.options[0];
        const mods = resolveMods(only.productId, only.defaultModifiers);
        for (let i = 0; i < slot.quantity; i++) {
          start[slot.id].push({ productId: only.productId, name: only.name, modifiers: mods });
        }
      }
    }
    return start;
  });

  // The item whose choices are being asked, if any: { slotId, option, index }.
  // index is the unit being changed, or null when a new one is being added.
  const [configuring, setConfiguring] = useState(null);

  const countIn = (slotId) => units[slotId]?.length ?? 0;
  const complete = slots.every((slot) => countIn(slot.id) === slot.quantity);

  function addUnit(slotId, option, modifiers = []) {
    setUnits((current) => ({
      ...current,
      [slotId]: [
        ...(current[slotId] ?? []),
        { productId: option.productId, name: option.name, modifiers },
      ],
    }));
  }

  function setUnitAt(slotId, index, modifiers) {
    setUnits((current) => ({
      ...current,
      [slotId]: (current[slotId] ?? []).map((u, i) => (i === index ? { ...u, modifiers } : u)),
    }));
  }

  function pick(slot, option) {
    if (countIn(slot.id) >= slot.quantity) return; // part already full
    const mods = resolveMods(option.productId, option.defaultModifiers);
    // With a saved default the item just drops in on it — no size prompt. Only
    // an item that has choices but no default set is asked outright.
    if (groupsFor(option.productId).length && mods.length === 0) {
      setConfiguring({ slotId: slot.id, option, index: null });
    } else {
      addUnit(slot.id, option, mods);
    }
  }

  // Reopen one chosen unit's choices, pre-filled with what it is set to.
  function changeUnit(slot, unit, index) {
    const option =
      slot.options.find((o) => o.productId === unit.productId) ?? {
        productId: unit.productId,
        name: unit.name,
      };
    setConfiguring({ slotId: slot.id, option, index });
  }

  function removeUnit(slotId, index) {
    setUnits((current) => ({
      ...current,
      [slotId]: (current[slotId] ?? []).filter((_, i) => i !== index),
    }));
  }

  function confirm() {
    // The same item with the same choices is one component, counted; a different
    // size (or a different item) is its own. Names ride along for the bill.
    const byPick = new Map();
    for (const slot of slots) {
      for (const unit of units[slot.id] ?? []) {
        const key =
          unit.productId + '|' + unit.modifiers.map((m) => m.optionId).sort().join(',');
        const found = byPick.get(key);
        if (found) found.quantity += 1;
        else
          byPick.set(key, {
            productId: unit.productId,
            name: unit.name,
            quantity: 1,
            modifiers: unit.modifiers.map((m) => ({ optionId: m.optionId })),
            modifierNames: unit.modifiers.map((m) => m.name),
          });
      }
    }
    onDone([...byPick.values()]);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Build ${product.name}`}
      className="fixed inset-0 z-20 flex items-end justify-center bg-black/70 sm:items-center"
    >
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-surface p-5 sm:rounded-2xl">
        <h2 className="font-medium text-ink text-lg">{product.name}</h2>
        <p className="mb-4 text-sm text-ink-3">{formatPKR(Number(product.price) || 0)} · choose what is inside</p>

        {slots.map((slot) => {
          const chosen = units[slot.id] ?? [];
          const full = chosen.length >= slot.quantity;

          if (isFixed(slot)) {
            return (
              <fieldset key={slot.id} className="mb-5">
                <legend className="mb-2 flex w-full items-baseline justify-between gap-3">
                  <span className="text-sm font-medium text-ink">{slot.label}</span>
                  <span className="text-xs text-ink-3">{slot.quantity} included</span>
                </legend>
                <div className="flex items-center justify-between rounded-lg border border-rule bg-surface-3 px-3 py-2.5">
                  <span className="text-sm text-ink">{slot.options[0].name}</span>
                  <span className="text-sm tabular-nums text-ink-2">{slot.quantity}×</span>
                </div>
              </fieldset>
            );
          }

          return (
            <fieldset key={slot.id} className="mb-5">
              <legend className="mb-2 flex w-full items-baseline justify-between gap-3">
                <span className="text-sm font-medium text-ink">{slot.label}</span>
                <span className={`text-xs ${full ? 'text-ink-3' : 'text-warn'}`}>
                  Choose {slot.quantity} · {chosen.length}/{slot.quantity}
                </span>
              </legend>

              {/* What has been chosen so far, each with its own choices. */}
              {chosen.length > 0 && (
                <ul className="mb-2 space-y-1">
                  {chosen.map((unit, i) => {
                    const hasChoices = groupsFor(unit.productId).length > 0;
                    return (
                      <li
                        key={i}
                        className="flex items-center gap-2 rounded-lg border border-brand-400/40 bg-brand-400/10 px-3 py-2"
                      >
                        <span className="min-w-0 flex-1 text-sm text-ink">
                          {unit.name}
                          {unit.modifiers.length > 0 && (
                            <span className="text-ink-2"> · {unit.modifiers.map((m) => m.name).join(', ')}</span>
                          )}
                        </span>
                        {hasChoices && (
                          <button
                            type="button"
                            onClick={() => changeUnit(slot, unit, i)}
                            aria-label={`Change ${unit.name}`}
                            className="rounded-md px-2 py-0.5 text-xs font-medium text-brand-400 hover:bg-surface-3"
                          >
                            Change
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeUnit(slot.id, i)}
                          aria-label={`Remove ${unit.name}`}
                          className="rounded-md px-2 py-0.5 text-xs font-medium text-ink-2 hover:bg-surface-3"
                        >
                          Remove
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* The items that can fill it — tap to add one on its default
                  choices (or to be asked, if it has none saved). Greyed when the
                  part is full. */}
              <div className="flex flex-wrap gap-2">
                {slot.options.map((option) => (
                  <button
                    key={option.productId}
                    type="button"
                    disabled={full}
                    onClick={() => pick(slot, option)}
                    className={`rounded-lg border px-3 py-2 text-sm ${
                      full
                        ? 'border-rule text-ink-3 opacity-50'
                        : 'border-rule bg-surface-3 text-ink hover:border-brand-400'
                    }`}
                  >
                    + {option.name}
                    {groupsFor(option.productId).length > 0 && (
                      <span className="text-ink-3"> …</span>
                    )}
                  </button>
                ))}
              </div>
            </fieldset>
          );
        })}

        {!complete && (
          <p className="mb-3 rounded-lg bg-warn-bg px-3.5 py-2.5 text-sm text-warn">
            Finish choosing for each part of the deal.
          </p>
        )}

        <div className="flex gap-2">
          <Button variant="secondary" size="lg" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="lg" className="flex-1" disabled={!complete} onClick={confirm}>
            Add to the order
          </Button>
        </div>
      </div>

      {/* Asking one chosen item's own size / extras — pre-filled with what it is
          set to when a placed unit is being changed. */}
      {configuring && (
        <ChoicePicker
          product={{ id: configuring.option.productId, name: configuring.option.name, price: 0 }}
          groups={groupsFor(configuring.option.productId)}
          hidePrices
          confirmLabel={configuring.index == null ? 'Add this one' : 'Save choices'}
          initial={
            configuring.index == null
              ? null
              : (units[configuring.slotId]?.[configuring.index]?.modifiers ?? []).map(
                  (m) => m.optionId
                )
          }
          onCancel={() => setConfiguring(null)}
          onDone={(modifiers) => {
            if (configuring.index == null) addUnit(configuring.slotId, configuring.option, modifiers);
            else setUnitAt(configuring.slotId, configuring.index, modifiers);
            setConfiguring(null);
          }}
        />
      )}
    </div>
  );
}
